import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import {
    Icon,
    IconClose,
    IconEventNote,
    IconPopOut
} from '../../../base/icons';
import { getParticipantDisplayName } from '../../../base/participants';
import { getBrandingColorPalette } from '../../../base/styles';
import {
    ColoredIcon,
    Header
} from '../../../participants-pane/components/styled';
import theme from '../../../participants-pane/theme.json';
import { FLUSH_NOTES } from '../../actionTypes';
import { closeNotesPane, loadNotesFromStorage, setNotesText, viewSharedNotes } from '../../actions';
import { getNotesPaneOpen } from '../../functions';
import { openNotesWindow } from '../../notesWindow.web';

import NotesContent from './NotesContent';
import NotesShare from './NotesShare';
import SharedNotesView from './SharedNotesView';

/* global interfaceConfig */

/**
 * Side pane for private, per-room conference notes stored in localStorage.
 *
 * @returns {React$Element}
 */
export const NotesPane = () => {
    const dispatch = useDispatch();
    const store = useStore();
    const [ windowError, setWindowError ] = useState('');
    const { t } = useTranslation();
    const paneOpen = useSelector(getNotesPaneOpen);
    const {
        text,
        richText,
        roomName,
        saveStatus,
        shareAudience,
        sharedNotes,
        sharedWith,
        viewing
    } = useSelector(state => state['features/notes']);
    const senders = Object.keys(sharedNotes);
    const shared = viewing && sharedNotes[viewing];
    const privacyLabel = shareAudience === 'everyone'
        ? t('notesPane.sharedEveryone')
        : sharedWith.length ? t('notesPane.sharedWith', { count: sharedWith.length }) : undefined;

    const onChange = useCallback((nextText, document) => {
        dispatch(setNotesText(nextText, document));
    }, [ dispatch ]);

    useEffect(() => {
        const flush = () => dispatch({ type: FLUSH_NOTES });
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                flush();
            }
        };

        window.addEventListener('pagehide', flush);
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            window.removeEventListener('pagehide', flush);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            flush();
        };
    }, [ dispatch ]);

    const closePane = useCallback(() => {
        dispatch(closeNotesPane());
    }, [ dispatch ]);

    const participantPaneStyle = {
        padding: paneOpen ? '0px 10px 0px 0px' : '0px'
    };
    const iconColor = interfaceConfig.applyMeetingSettings
        ? getBrandingColorPalette(interfaceConfig).accent1
        : '#ffffff';

    return (
        <ThemeProvider theme = { theme }>
            <div
                className = 'participants_pane notes_pane'
                style = { participantPaneStyle }>
                <div className = 'participants_pane-content'>
                    <Header
                        theme = {{
                            headerBackground: getBrandingColorPalette(interfaceConfig)?.primary
                        }}>
                        <div className = 'participant-pane-heading'>
                            <div className = 'notes_pane-heading-group'>
                                <ColoredIcon color = { iconColor }>
                                    <Icon
                                        className = 'participant_pane-header-icon'
                                        color = { iconColor }
                                        fillColor = { iconColor }
                                        size = { 27 }
                                        src = { IconEventNote } />
                                </ColoredIcon>
                                <div className = 'participants_page_heading'>
                                    {t('notesPane.title')}
                                </div>
                            </div>
                            <div className = 'notes_pane-heading-group'>
                                <button
                                    aria-label = { t('notesPane.openWindow') }
                                    className = 'notes_pane-popout'
                                    onClick = { () => {
                                        setWindowError('');
                                        openNotesWindow(store, t, () => setWindowError(t('notesPane.windowFailed')));
                                    } }
                                    title = { t('notesPane.openWindow') }
                                    type = 'button'>
                                    <Icon
                                        color = { iconColor }
                                        size = { 20 }
                                        src = { IconPopOut } />
                                </button>
                                <button
                                    aria-label = { t('toolbar.closeNotesPane') }
                                    className = 'notes_pane-close'
                                    onClick = { closePane }
                                    type = 'button'>
                                    <Icon
                                        color = { iconColor }
                                        size = { 20 }
                                        src = { IconClose } />
                                </button>
                            </div>
                        </div>
                    </Header>
                    {windowError && <p
                        className = 'notes_pane-window-error'
                        role = 'alert'>{windowError}</p>}
                    {senders.length > 0 && (
                        <div className = 'notes_pane-tabs'>
                            {[ null, ...senders ].map(id => (
                                <button
                                    aria-pressed = { (shared ? viewing : null) === id }
                                    key = { id || 'mine' }
                                    onClick = { () => dispatch(viewSharedNotes(id)) }
                                    type = 'button'>
                                    {id ? getParticipantDisplayName(store.getState(), id) : t('notesPane.myNotes')}
                                </button>
                            ))}
                        </div>
                    )}
                    {shared ? (
                        <SharedNotesView
                            key = { viewing }
                            ownerId = { viewing }
                            permission = { shared.permission }
                            revision = { shared.revision }
                            richText = { shared.richText }
                            t = { t }
                            text = { shared.text } />
                    ) : (
                        <NotesContent
                            onChange = { onChange }
                            onRetry = { () => dispatch(saveStatus === 'loadError'
                                ? loadNotesFromStorage(roomName) : { type: FLUSH_NOTES }) }
                            privacyLabel = { privacyLabel }
                            richText = { richText }
                            roomName = { roomName }
                            saveStatus = { saveStatus }
                            showPrivacyChip = { false }
                            t = { t }
                            text = { text }>
                            <NotesShare t = { t } />
                        </NotesContent>
                    )}
                </div>
            </div>
        </ThemeProvider>
    );
};

export default NotesPane;
